import type { UniDriver } from '@wix/wix-ui-test-utils/unidriver';
import {
  DropdownUniDriver,
  ListItemSectionUniDriver,
} from '@wix/design-system/dist/testkit/unidriver';
import { baseUniDriverFactory } from '../../unidriver';
import { ViewsDropdownListItemUniDriver } from './ViewsDropdownListItem.uni.driver';

export const CollectionViewsDropdownUniDriver = (
  base: UniDriver,
  body: UniDriver,
) => {
  const viewsDropdownDriverBase = () =>
    base.$('[data-hook="collection-views-dropdown"]');

  const getDropdownLayoutBase = async () =>
    body.$(
      `[data-content-element="${await viewsDropdownDriverBase().attr(
        'data-content-hook',
      )}"]`,
    );

  const viewsDropdownDriver = () =>
    DropdownUniDriver(viewsDropdownDriverBase(), body);

  const getViewListItemById = (id: string) => {
    return ViewsDropdownListItemUniDriver(
      viewsDropdownDriverBase().$(`[data-hook="dropdown-item-${id}"]`),
      body,
    );
  };

  const getViewListItemAt = (index: number) => {
    return ViewsDropdownListItemUniDriver(
      viewsDropdownDriverBase().$$('[data-hook="view-item"]').get(index),
      body,
    );
  };

  const getCategoryListItemById = (id: string) => {
    return ListItemSectionUniDriver(
      viewsDropdownDriverBase().$(`[data-hook="dropdown-item-${id}"]`),
      body,
    );
  };

  const getCategoryListItemAt = (index: number) => {
    return ListItemSectionUniDriver(
      viewsDropdownDriverBase().$$('[data-hook="view-category"]').get(index),
      body,
    );
  };

  return {
    ...baseUniDriverFactory(base),
    _input: () => viewsDropdownDriverBase().$('input'),
    exists: () => viewsDropdownDriver().exists(),
    initialLoaderExists: async () =>
      (await getDropdownLayoutBase())
        .$('[data-hook="initial-loader"]')
        .exists(),
    /** Get the currently selected view name */
    getSelected: () => viewsDropdownDriver().inputDriver.getValue(),
    /** Get the dropdown input text */
    getInputValue: () => viewsDropdownDriver().inputDriver.getValue(),
    /** Indicates input is ready-only */
    getInputReadOnly: () => viewsDropdownDriver().inputDriver.getReadOnly(),
    /** Indicates input is focused */
    isInputFocused: () => viewsDropdownDriver().inputDriver.isFocus(),
    /** Get the currently selected view id */
    getSelectedId: () =>
      viewsDropdownDriver().dropdownLayoutDriver.getSelectedOptionId(),
    /** Checks if total is shown */
    hasTotal: () =>
      viewsDropdownDriver().inputDriver.suffixComponentExists('.total-count'),
    /** Opens views dropdown */
    open: () => viewsDropdownDriver().inputDriver.click(),
    /** Enter dropdown input text */
    enterText: (value: string) =>
      viewsDropdownDriver().inputDriver.enterText(value),
    /** Click dropdown input clear button */
    clickInputClearButton: () => viewsDropdownDriver().inputDriver.clickClear(),
    /** Indicates input clear button exists  */
    inputClearButtonExists: () =>
      viewsDropdownDriver().inputDriver.hasClearButton(),
    /** Get number of views */
    getViewsCount: async () =>
      viewsDropdownDriverBase().$$('[data-hook="view-item"]').count(),
    /** Get array of all view names in the dropdown */
    getViewsContent: async () =>
      viewsDropdownDriverBase().$$('[data-hook="view-item"]').text(),
    /** Get array popover content , like empty state */
    getViewsPopoverContent: async () =>
      viewsDropdownDriverBase().$$('[data-hook="popover-content"]').text(),
    /** @deprecated use getViewsCount for better experience
     * Get number of options in the dropdown
     */
    getOptionsLength: async () =>
      viewsDropdownDriver().dropdownLayoutDriver.optionsLength(),
    /** @deprecated use getViewsContent for better experience
     * Get array of all options in the dropdown
     */
    getOptions: async () =>
      viewsDropdownDriver().dropdownLayoutDriver.optionsContent(),
    /** Indicates whether the dropdown is open */
    isOpen: () => viewsDropdownDriver().dropdownLayoutDriver.isShown(),
    /** Get the placeholder test of the dropdown */
    getPlaceholderText: () =>
      viewsDropdownDriver().inputDriver.getPlaceholder(),
    /** Get view list item driver by view id */
    getViewListItemById,
    /** Get view list item driver by view index */
    getViewListItemAt,
    /** Get category list item driver by category id */
    getCategoryListItemById,
    /** Get category list item driver by category index */
    getCategoryListItemAt,
    /** Get "all items" view list item */
    getAllItemsView: () => getCategoryListItemById('all-items-view'),
    /** Get number of categories */
    getCategoriesCount: async () =>
      viewsDropdownDriverBase().$$('[data-hook="view-category"]').count(),
    /** Get array of all category names in the dropdown */
    getCategoriesContent: async () =>
      viewsDropdownDriverBase().$$('[data-hook="view-category"]').text(),
    /** Check if view pending changes */
    isViewPendingChanges: async () =>
      viewsDropdownDriver().inputDriver.prefixComponentExists(
        '[data-hook="view-pending-changes"]',
      ),
    isDefaultView: async () =>
      viewsDropdownDriver().inputDriver.prefixComponentExists(
        '[data-hook="view-default"]',
      ),
  };
};
